/**
 * Reports leave the app on paper or as a PDF, and the dark theme does not
 * survive the trip: the ground turns into a slab of ink, the brand red goes
 * pink against it, and whoever opens the file gets a document that looks
 * nothing like the one everyone else sends. So print is always light,
 * whatever the person is reading the app in.
 *
 * Every dark rule in the stylesheets hangs off one of two gates —
 * `:root[data-theme="dark"]`, or `:root:not([data-theme="light"])` inside the
 * system-preference query. Stamping data-theme="light" on <html> closes both
 * at once, so this is the whole fix: no print copy of the dark tokens to keep
 * in step, and every report that prints the live page is covered by it.
 */

/** The theme to put back afterwards; `undefined` while nothing is printing. */
let stashed: string | null | undefined;

function forceLight(): void {
  if (stashed !== undefined) return;
  stashed = document.documentElement.getAttribute('data-theme');
  document.documentElement.setAttribute('data-theme', 'light');
}

function restore(): void {
  if (stashed === undefined) return;
  /* No attribute means the person is on 'system' — put it back by taking the
     attribute away again, not by stamping a value the toggle never set. */
  if (stashed === null) document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', stashed);
  stashed = undefined;
}

window.addEventListener('beforeprint', forceLight);
window.addEventListener('afterprint', restore);

/* Safari fires neither event; all it does is flip the print media query, so
   watch that too. The guards above make the overlap harmless. */
const printing = window.matchMedia?.('print');
printing?.addEventListener?.('change', (event) => {
  if (event.matches) forceLight();
  else restore();
});
